import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Alert, Backdrop, Button, Collapse, IconButton } from '@mui/material'
import { Cancel, CheckCircle, ExpandMore, QrCode2, Replay } from '@mui/icons-material'

export const ETAPA_AGUARDANDO = 'aguardando'
export const ETAPA_PROCESSANDO = 'processando'
export const ETAPA_CONCLUIDA = 'concluido'
export const ETAPA_FALHOU = 'falhou'

const SEQUENCIA_ETAPAS = [
	{ id: 'cheguei', titulo: 'Cheguei' },
	{ id: 'finalizar', titulo: 'Finalizou' }
]

const APARENCIA = {
	[ETAPA_AGUARDANDO]: {
		texto: 'Aguardando etapa anterior',
		fundo: 'bg-white',
		borda: 'border-gray-200',
		textoCor: 'text-gray-400'
	},
	[ETAPA_PROCESSANDO]: {
		texto: 'Processando...',
		fundo: 'bg-white',
		borda: 'border-gray-200',
		textoCor: 'text-gray-500'
	},
	[ETAPA_CONCLUIDA]: {
		texto: 'Concluído',
		fundo: 'bg-green-50',
		borda: 'border-green-100',
		textoCor: 'text-green-700'
	},
	[ETAPA_FALHOU]: {
		texto: 'Falhou',
		fundo: 'bg-red-50',
		borda: 'border-red-100',
		textoCor: 'text-red-700'
	}
}

/** Anel girando puro CSS: o CircularProgress do MUI não acompanha o tamanho do ícone das etapas. */
function Girando() {
	return (
		<span
			className="inline-block rounded-full border-2 border-blue-500 border-t-transparent animate-spin"
			style={{ width: 22, height: 22 }}
		/>
	)
}

function IconeEtapa({ status }) {
	if (status === ETAPA_CONCLUIDA)
		return <CheckCircle className="text-green-600" />

	if (status === ETAPA_FALHOU)
		return <Cancel className="text-red-600" />

	return <Girando />
}

function LinhaEtapa({ titulo, status, detalhe }) {
	const [aberto, setAberto] = useState(false)
	const aparencia = APARENCIA[status] ?? APARENCIA[ETAPA_AGUARDANDO]

	return (
		<div className={`border rounded-lg ${aparencia.fundo} ${aparencia.borda}`}>
			<div className="flex items-center gap-3 px-3 py-2">
				<IconeEtapa status={status} />

				<div className="flex-1 min-w-0">
					<p className="m-0 font-medium text-gray-800 text-sm">{titulo}</p>
					<p className={`m-0 text-xs ${aparencia.textoCor}`}>
						{detalhe || aparencia.texto}
					</p>
				</div>

				{/* Só existe quando há explicação a mais: seta que não abre nada frustra o profissional */}
				{detalhe && (
					<IconButton size="small" onClick={() => setAberto(!aberto)} aria-label="Mais detalhes">
						<ExpandMore
							fontSize="small"
							style={{ transform: aberto ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
						/>
					</IconButton>
				)}
			</div>

			{detalhe && (
				<Collapse in={aberto}>
					<p className="m-0 px-3 pb-2 text-xs text-gray-600">{detalhe}</p>
				</Collapse>
			)}
		</div>
	)
}

/**
 * Acompanhamento da finalização por leitura de código.
 *
 * Mostra em que etapa o processo está para o profissional não ficar olhando um
 * spinner sem contexto quando a chegada ou a finalização é recusada.
 *
 * @param {object} etapas  { cheguei: {status, detalhe}, finalizar: {status, detalhe} }
 * @param {string} erro    mensagem de bloqueio (raio, ordem de pontos, etc.)
 */
export default function FinalizarPontoProgresso({
	open,
	etapas,
	erro = '',
	onFechar,
	onTentarNovamente
}) {
	const falhou = SEQUENCIA_ETAPAS.some(({ id }) => etapas?.[id]?.status === ETAPA_FALHOU)
	const concluido = SEQUENCIA_ETAPAS.every(({ id }) => etapas?.[id]?.status === ETAPA_CONCLUIDA)

	// Portal no body: o motion.div das telas aplica transform e viraria o bloco
	// de contenção do position: fixed do Backdrop, recortando o modal.
	return createPortal(
		<Backdrop open={open} sx={{ zIndex: 12000, alignItems: 'center', justifyContent: 'center' }}>
			<div
				className="mx-3 bg-white rounded-xl overflow-hidden flex flex-col"
				style={{ width: 'min(100%, 380px)', maxHeight: 'calc(100vh - 1.5rem)' }}
			>
				<div className="overflow-y-auto px-4 pt-5 pb-4">
					<div className="flex justify-center">
						<div className="relative">
							<div className="rounded-full bg-indigo-50 flex items-center justify-center" style={{ width: 64, height: 64 }}>
								<QrCode2 className="text-indigo-900" style={{ fontSize: 34 }} />
							</div>

							{(falhou || concluido) && (
								<span className="absolute" style={{ right: -2, bottom: -2 }}>
									{falhou
										? <Cancel className="text-red-600 bg-white rounded-full" />
										: <CheckCircle className="text-green-600 bg-white rounded-full" />}
								</span>
							)}
						</div>
					</div>

					<h6 className="text-center font-semibold text-gray-900 mt-3 mb-1">
						Finalizando ponto
					</h6>
					<p className="text-center text-sm text-gray-500 m-0 mb-4">
						{falhou ? 'Verificando etapas para concluir' : 'Aguarde a conclusão das etapas'}
					</p>

					<div className="flex flex-col gap-2">
						{SEQUENCIA_ETAPAS.map(({ id, titulo }) => (
							<LinhaEtapa
								key={id}
								titulo={titulo}
								status={etapas?.[id]?.status ?? ETAPA_AGUARDANDO}
								detalhe={etapas?.[id]?.detalhe}
							/>
						))}
					</div>

					{erro && (
						<Alert severity="error" className="mt-3" sx={{ alignItems: 'center' }}>
							{erro}
						</Alert>
					)}
				</div>

				{falhou && (
					<div className="px-4 pb-4 pt-0 flex gap-2 flex-shrink-0">
						{onTentarNovamente && (
							<Button variant="contained" fullWidth startIcon={<Replay />} onClick={onTentarNovamente}>
								Ler novamente
							</Button>
						)}
						<Button variant="outlined" color="inherit" fullWidth onClick={onFechar}>
							Fechar
						</Button>
					</div>
				)}
			</div>
		</Backdrop>,
		document.body
	)
}
